import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight,
  Star,
  CheckCircle2,
  Sparkles,
  Bot,
  Zap,
  Image as ImageIcon,
  Mic,
  Send,
  TrendingUp,
  Shield,
  Lock,
  Flame,
  Users,
} from 'lucide-react';
import ContactForm from '../components/ContactForm';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const stats = [
  { label: 'Customers', value: '12.5K+' },
  { label: 'AI Posts Made', value: '380K+' },
  { label: 'Average Rating', value: '4.9/5' },
  { label: 'Hours Saved/Week', value: '20+' },
];

const aiCapabilities = [
  { icon: Bot, title: 'GPT-5.1 Copywriting', desc: 'Viral captions and posts generated in seconds' },
  { icon: ImageIcon, title: 'AI Image Generation', desc: 'Studio-quality visuals on demand' },
  { icon: Mic, title: 'Voice-to-Post', desc: 'Talk it out — Whisper turns audio into posts' },
  { icon: Send, title: 'Auto-Post FB + IG', desc: 'Schedule or post instantly to your pages' },
];

const Home = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/products`)
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]));
  }, []);

  const featured = products.slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#11111a] to-[#0a0a0f] text-white relative overflow-hidden" data-testid="home-page">
      {/* Ambient blobs */}
      <div className="pointer-events-none absolute top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-orange-500/20 blur-[140px]" />
      <div className="pointer-events-none absolute top-1/3 -right-32 w-[32rem] h-[32rem] rounded-full bg-red-600/20 blur-[160px]" />
      <div className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] rounded-full bg-amber-500/10 blur-[180px]" />

      {/* Hero */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs font-semibold" data-testid="hero-pill">
                <Sparkles className="w-3.5 h-3.5" /> AI-POWERED MONEY MACHINE
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
                <span className="bg-gradient-to-r from-orange-400 via-amber-300 to-red-500 bg-clip-text text-transparent">
                  Print Money With AI.
                </span>
                <br />
                <span className="text-white">Auto-Post. Auto-Earn.</span>
              </h1>

              <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-xl">
                Premium AI playbooks + a built-in <strong className="text-white">AI Auto-Poster</strong> that generates viral content and posts it directly to your Facebook &amp; Instagram. 12,000+ creators use it to make money in their sleep.
              </p>

              <div className="flex flex-wrap gap-3">
                <Link to="/marketplace">
                  <Button size="lg" className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 font-bold" data-testid="hero-cta-marketplace">
                    Explore AI Marketplace
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/ai-auto-poster">
                  <Button size="lg" variant="outline" className="border-white/15 bg-white/5 text-white hover:bg-white/10 hover:border-orange-500/60" data-testid="hero-cta-autoposter">
                    <Bot className="mr-2 w-5 h-5" /> Try AI Auto-Poster
                  </Button>
                </Link>
              </div>

              {/* Trust pills */}
              <div className="flex flex-wrap gap-4 pt-2 text-xs text-white/60">
                <span className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-400" /> 30-Day Guarantee</span>
                <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-emerald-400" /> Stripe Secure Checkout</span>
                <span className="inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-emerald-400" /> Instant Delivery</span>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-white/10">
                {stats.map((stat) => (
                  <div key={stat.label}>
                    <div className="text-2xl md:text-3xl font-black bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
                      {stat.value}
                    </div>
                    <div className="text-xs text-white/60 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative">
              <div className="relative z-10 rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-6 backdrop-blur-md">
                <div className="absolute top-4 right-4">
                  <Badge className="bg-emerald-500 text-white border-0 shadow-lg flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
                  </Badge>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-white/60">AI Auto-Poster</div>
                    <div className="text-base font-bold">Generating Instagram post...</div>
                  </div>
                </div>
                <div className="rounded-2xl bg-black/40 border border-white/10 p-4 mb-3 text-sm text-white/85 leading-relaxed">
                  <span className="text-orange-300 text-xs font-bold">CAPTION DRAFT</span>
                  <p className="mt-2">
                    &quot;Stop trading hours for dollars. AI just wrote this caption, designed the image, and is about to post it to my IG &mdash; while I sleep. Welcome to 2026. 🚀&quot;
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {['#AISideHustle', '#PassiveIncome', '#GPT51', '#AutoPost'].map((t) => (
                      <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/30">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {aiCapabilities.map((cap) => {
                    const Icon = cap.icon;
                    return (
                      <div key={cap.title} className="rounded-xl bg-white/5 border border-white/10 p-3 text-center">
                        <Icon className="w-5 h-5 mx-auto text-orange-400" />
                        <div className="text-[10px] text-white/70 mt-1">{cap.title.split(' ')[0]}</div>
                      </div>
                    );
                  })}
                </div>
                <Button className="w-full mt-4 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold" disabled>
                  <Send className="w-4 h-4 mr-2" /> Auto-Post to Facebook + Instagram
                </Button>
              </div>
              <div className="absolute -top-6 -right-6 w-72 h-72 bg-gradient-to-br from-orange-400 to-red-500 rounded-full blur-3xl opacity-25" />
              <div className="absolute -bottom-6 -left-6 w-72 h-72 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full blur-3xl opacity-25" />
            </div>
          </div>
        </div>
      </section>

      {/* What AI Does */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-orange-500/10 border border-orange-500/30 text-orange-300 hover:bg-orange-500/15">What AI Does For You</Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight">
              <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">AI does the work.</span>
              <span className="text-white"> You collect the bag.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {aiCapabilities.map((cap) => {
              const Icon = cap.icon;
              return (
                <Card key={cap.title} className="bg-white/5 border border-white/10 backdrop-blur-md hover:border-orange-500/50 transition-all group">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-white mt-4">{cap.title}</h3>
                    <p className="text-sm text-white/60 mt-1">{cap.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured AI Products */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-between items-end gap-4 mb-10">
            <div>
              <Badge className="mb-3 bg-orange-500/10 border border-orange-500/30 text-orange-300 hover:bg-orange-500/15">Featured AI Products</Badge>
              <h2 className="text-3xl md:text-4xl font-black">
                <span className="text-white">Best-Selling </span>
                <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">AI Playbooks</span>
              </h2>
            </div>
            <Link to="/marketplace">
              <Button variant="outline" className="border-orange-500/40 bg-orange-500/10 text-orange-300 hover:bg-orange-500/20" data-testid="featured-view-all">
                View All <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {featured.map((p) => (
              <Card key={p.id} className="overflow-hidden bg-white/5 border border-white/10 backdrop-blur-md hover:border-orange-500/50 transition-all group">
                <div className="relative h-48 overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <Badge className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-red-600 text-white border-0 shadow-lg">
                    <Bot className="w-3 h-3 mr-1" /> AI POWERED
                  </Badge>
                  <Badge className="absolute top-3 right-3 bg-black/60 text-white border border-white/20">${p.price}</Badge>
                </div>
                <CardContent className="p-5">
                  <h3 className="text-base font-bold text-white leading-snug mb-2">{p.name}</h3>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1 text-yellow-400">
                      <Star className="w-3.5 h-3.5 fill-yellow-400" /> <span className="font-bold">{p.rating}</span>
                    </div>
                    <div className="text-white/60 flex items-center gap-1"><Users className="w-3 h-3" /> {p.downloads}</div>
                    <div className="text-emerald-400 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Hot</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-3 bg-orange-500/10 border border-orange-500/30 text-orange-300 hover:bg-orange-500/15">Trust &amp; Guarantee</Badge>
            <h2 className="text-3xl md:text-4xl font-black">
              <span className="text-white">Why Creators </span>
              <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">Choose Us</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-white/5 border border-white/10 backdrop-blur-md hover:border-orange-500/40 transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg mb-4">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <div className="text-2xl font-black text-white mb-1">30-Day Guarantee</div>
                <p className="text-sm text-white/70">Not seeing results? Get a full refund — no questions asked. We stake our reputation on it.</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10 backdrop-blur-md hover:border-orange-500/40 transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg mb-4">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <div className="text-2xl font-black text-white mb-1">Instant Delivery</div>
                <p className="text-sm text-white/70">Buy a guide, get access instantly. AI Auto-Poster subscribers unlock all 9 tools in under a minute.</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10 backdrop-blur-md hover:border-orange-500/40 transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center shadow-lg mb-4">
                  <Lock className="w-7 h-7 text-white" />
                </div>
                <div className="text-2xl font-black text-white mb-1">Stripe Secure</div>
                <p className="text-sm text-white/70">Powered by Stripe — the same checkout used by Amazon, Shopify, and Google. Your card never touches our servers.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-3 bg-orange-500/10 border border-orange-500/30 text-orange-300 hover:bg-orange-500/15">Get In Touch</Badge>
            <h2 className="text-3xl md:text-4xl font-black text-white">Let&apos;s Build Something Big</h2>
            <p className="text-base text-white/60 mt-3">
              Have a project in mind? Tell us how we can help you grow.
            </p>
          </div>
          <Card className="bg-white/5 border border-white/10 backdrop-blur-md shadow-2xl">
            <CardContent className="p-8">
              <ContactForm />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-5xl mx-auto">
          <Card className="relative overflow-hidden border-0 shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-red-600 to-amber-500" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,white,transparent_50%)] opacity-15" />
            <CardContent className="relative z-10 p-10 md:p-14 text-center text-white">
              <Flame className="w-12 h-12 mx-auto mb-4 text-white" />
              <h2 className="text-3xl md:text-5xl font-black mb-4">Ready to Print Money With AI?</h2>
              <p className="text-base md:text-lg mb-8 text-white/90">
                Stop watching others get rich. Plug into AI today &mdash; start making moves tonight.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/marketplace">
                  <Button size="lg" variant="secondary" className="bg-white text-orange-600 hover:bg-white/90 font-bold shadow-lg" data-testid="cta-browse">
                    Browse AI Products <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/ai-auto-poster">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-bold" data-testid="cta-autoposter">
                    <Bot className="mr-2 w-5 h-5" /> Start AI Auto-Poster
                  </Button>
                </Link>
              </div>
              <ul className="flex flex-wrap items-center justify-center gap-4 mt-6 text-xs text-white/85">
                <li className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Stripe secured</li>
                <li className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Instant delivery</li>
                <li className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> 30-day guarantee</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Home;
